import { useCallback, useEffect, useMemo, useState } from 'react'
import { FiEdit } from 'react-icons/fi'
import { useDispatch } from 'react-redux'

import { t } from 'i18next'

import { Button } from '~/components/Buttons'
import SubpublisherApprovalBadge from '~/modules/retailMedia/components/SubpublisherApprovalBadge'
import { patchNetworkDestinationUrls } from '~/modules/retailMedia/store/industry/campaign/actions'
import { useAppSelector } from '~/store/hooks'

import DestinationUrlRow from '../DestinationUrlRow'
import DestinationUrlsEditor from '../DestinationUrlsEditor'

export type SubpublisherDestinationUrlEntry = {
  url: string
  brand?: string
  name?: string
}

type DestinationLayoutMode = 'grouped' | 'flat'

const getDestinationLabel = ({
  item,
  publisherId,
  publisherName,
  getName
}: {
  item: SubpublisherDestinationUrlEntry
  publisherId: PublisherId
  publisherName: string
  getName: (publisherId: string, brand: string) => string | undefined
}) =>
  item.brand
    ? item.name || getName(publisherId, item.brand) || item.brand
    : publisherName

const useNetworkDestinationEditor = ({
  campaignId,
  publisherId,
  destinationUrls,
  patchKey,
  onSaved
}: {
  campaignId: CampaignId
  publisherId: PublisherId
  destinationUrls: SubpublisherDestinationUrlEntry[]
  patchKey: string
  onSaved: () => void
}) => {
  const dispatch = useDispatch()
  const [isEditing, setIsEditing] = useState(false)
  const [draftUrls, setDraftUrls] = useState(() =>
    destinationUrls.map(item => item.url)
  )
  const [displayUrls, setDisplayUrls] = useState(() =>
    destinationUrls.map(item => item.url)
  )
  const loadingPatchKeys = useAppSelector(
    state => state.retailMedia.industry.campaign.patch.loadingKeys
  )
  const isSaving = !!loadingPatchKeys?.includes(patchKey)

  useEffect(() => {
    setDisplayUrls(destinationUrls.map(item => item.url))
  }, [destinationUrls])

  useEffect(() => {
    if (!isEditing) {
      setDraftUrls(destinationUrls.map(item => item.url))
    }
  }, [destinationUrls, isEditing])

  const openEditor = useCallback(() => {
    setDraftUrls(destinationUrls.map(item => item.url))
    setIsEditing(true)
  }, [destinationUrls])

  const handleCancel = useCallback(() => {
    setDraftUrls(destinationUrls.map(item => item.url))
    setIsEditing(false)
  }, [destinationUrls])

  const handleChange = useCallback((index: number, url: string) => {
    setDraftUrls(current =>
      current.map((currentUrl, currentIndex) =>
        currentIndex === index ? url : currentUrl
      )
    )
  }, [])

  const handleSubmit = useCallback(() => {
    const destinationUrlsPayload = destinationUrls.map(
      (destination, destinationIndex) => {
        const url = draftUrls[destinationIndex] ?? destination.url
        return destination.brand ? { url, brand: destination.brand } : { url }
      }
    )

    dispatch(
      patchNetworkDestinationUrls({
        id: campaignId,
        body: {
          publisher_id: publisherId,
          destination_urls: destinationUrlsPayload
        },
        patchKey,
        successFeedbackMsg: t('rm:campaign.successfully.updated'),
        onSuccess: () => {
          setDisplayUrls(draftUrls)
          setIsEditing(false)
          onSaved()
        }
      })
    )
  }, [
    campaignId,
    destinationUrls,
    dispatch,
    draftUrls,
    onSaved,
    patchKey,
    publisherId
  ])

  return {
    displayUrls,
    draftUrls,
    handleCancel,
    handleChange,
    handleSubmit,
    isEditing,
    isSaving,
    openEditor
  }
}

const StatusSlot = ({
  blockedStatus,
  statusPending,
  rejectionReason,
  publisherName,
  contextName
}: {
  blockedStatus?: 'waiting' | 'rejected'
  statusPending?: boolean
  rejectionReason?: string
  publisherName: string
  contextName: string
}) => {
  if (!blockedStatus && !statusPending) return null

  return (
    <div data-testid="subpublisher-destination-status">
      <SubpublisherApprovalBadge
        status={blockedStatus}
        reason={rejectionReason}
        publisherName={publisherName}
        contextName={contextName}
      />
    </div>
  )
}

const EditAction = ({
  label,
  onClick
}: {
  label: string
  onClick: () => void
}) => (
  <Button
    size="xsmall"
    template="secondary"
    customWidth="auto"
    iconLeft={<FiEdit />}
    tooltip={t('c:actions.edit')}
    aria-label={`${t('c:actions.edit')}: ${label}`}
    onClick={onClick}
    data-testid="subpublisher-destination-edit"
  />
)

const GroupedPublisherDestination = ({
  campaignId,
  publisherId,
  publisherName,
  destinationUrls,
  getName,
  canEdit,
  blockedStatus,
  statusPending,
  rejectionReason,
  onSaved
}: {
  campaignId: CampaignId
  publisherId: PublisherId
  publisherName: string
  destinationUrls: SubpublisherDestinationUrlEntry[]
  getName: (publisherId: string, brand: string) => string | undefined
  canEdit: boolean
  blockedStatus?: 'waiting' | 'rejected'
  statusPending?: boolean
  rejectionReason?: string
  onSaved: () => void
}) => {
  const editor = useNetworkDestinationEditor({
    campaignId,
    publisherId,
    destinationUrls,
    patchKey: `destination_url_${campaignId}_${publisherId}_publisher`,
    onSaved
  })

  const entries = destinationUrls.map((item, index) => {
    const label = getDestinationLabel({
      item,
      publisherId,
      publisherName,
      getName
    })

    return {
      ...item,
      url: editor.draftUrls[index] ?? item.url,
      name: item.brand ? label : undefined,
      inputLabel: label
    }
  })

  return (
    <div
      className={`d-flex flex-column gap-2${
        editor.isEditing ? ' border-top border-bottom py-3' : ''
      }`}
      data-testid={
        editor.isEditing ? 'subpublisher-destination-editor' : undefined
      }
    >
      <header className="d-flex align-items-end justify-content-between gap-2">
        <span className="fw-bold">{publisherName}</span>

        {canEdit && !editor.isEditing && (
          <EditAction label={publisherName} onClick={editor.openEditor} />
        )}

        <StatusSlot
          blockedStatus={blockedStatus}
          statusPending={statusPending}
          rejectionReason={rejectionReason}
          publisherName={publisherName}
          contextName={publisherName}
        />
      </header>

      {editor.isEditing ? (
        <DestinationUrlsEditor
          entries={entries}
          onChange={editor.handleChange}
          onCancel={editor.handleCancel}
          onSubmit={editor.handleSubmit}
          isSaving={editor.isSaving}
          ariaLabel={publisherName}
        />
      ) : (
        <div className="d-flex flex-column gap-3">
          {destinationUrls.map((item, index) => {
            const label = getDestinationLabel({
              item,
              publisherId,
              publisherName,
              getName
            })

            return (
              <DestinationUrlRow
                key={`${publisherId}-${item.brand ?? index}`}
                url={editor.displayUrls[index] ?? item.url}
                name={item.brand ? label : undefined}
                inputLabel={label}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

const FlatDestinationItem = ({
  campaignId,
  publisherId,
  publisherName,
  destinationUrls,
  itemIndex,
  getName,
  canEdit,
  blockedStatus,
  statusPending,
  rejectionReason,
  onSaved
}: {
  campaignId: CampaignId
  publisherId: PublisherId
  publisherName: string
  destinationUrls: SubpublisherDestinationUrlEntry[]
  itemIndex: number
  getName: (publisherId: string, brand: string) => string | undefined
  canEdit: boolean
  blockedStatus?: 'waiting' | 'rejected'
  statusPending?: boolean
  rejectionReason?: string
  onSaved: () => void
}) => {
  const item = destinationUrls[itemIndex]
  const label = getDestinationLabel({
    item,
    publisherId,
    publisherName,
    getName
  })
  const editor = useNetworkDestinationEditor({
    campaignId,
    publisherId,
    destinationUrls,
    patchKey: `destination_url_${campaignId}_${publisherId}_${
      item.brand ?? itemIndex
    }`,
    onSaved
  })

  return (
    <div
      className={`d-flex flex-column gap-2${
        editor.isEditing ? ' border-top border-bottom py-3' : ''
      }`}
      data-testid={
        editor.isEditing ? 'subpublisher-destination-editor' : undefined
      }
    >
      <header className="d-flex align-items-end justify-content-between gap-2">
        <span className="fw-bold">{label}</span>

        {canEdit && !editor.isEditing && (
          <EditAction label={label} onClick={editor.openEditor} />
        )}

        <StatusSlot
          blockedStatus={blockedStatus}
          statusPending={statusPending}
          rejectionReason={rejectionReason}
          publisherName={publisherName}
          contextName={label}
        />
      </header>

      {editor.isEditing ? (
        <DestinationUrlsEditor
          entries={[
            {
              ...item,
              url: editor.draftUrls[itemIndex] ?? item.url,
              name: undefined,
              inputLabel: label
            }
          ]}
          onChange={(_index, url) => editor.handleChange(itemIndex, url)}
          onCancel={editor.handleCancel}
          onSubmit={editor.handleSubmit}
          isSaving={editor.isSaving}
          ariaLabel={publisherName}
        />
      ) : (
        <DestinationUrlRow
          url={editor.displayUrls[itemIndex] ?? item.url}
          inputLabel={label}
        />
      )}
    </div>
  )
}

const SubpublisherDestinationSection = ({
  campaignId,
  publisherId,
  publisherName,
  rejectionReason,
  destinationUrls,
  getName,
  canEdit,
  blockedStatus,
  statusPending,
  layoutMode,
  visibleBrand,
  onSaved
}: {
  campaignId: CampaignId
  publisherId: PublisherId
  publisherName: string
  rejectionReason?: string
  destinationUrls: SubpublisherDestinationUrlEntry[]
  getName: (publisherId: string, brand: string) => string | undefined
  canEdit: boolean
  blockedStatus?: 'waiting' | 'rejected'
  statusPending?: boolean
  layoutMode: DestinationLayoutMode
  visibleBrand?: string | null
  onSaved: () => void
}) => {
  const selectedBrand = visibleBrand ?? undefined
  const rows = useMemo(
    () =>
      destinationUrls
        .map((item, index) => ({ item, index }))
        .filter(
          ({ item }) =>
            visibleBrand === undefined || item.brand === selectedBrand
        ),
    [destinationUrls, selectedBrand, visibleBrand]
  )

  if (layoutMode === 'grouped') {
    return (
      <GroupedPublisherDestination
        campaignId={campaignId}
        publisherId={publisherId}
        publisherName={publisherName}
        rejectionReason={rejectionReason}
        destinationUrls={destinationUrls}
        getName={getName}
        canEdit={canEdit}
        blockedStatus={blockedStatus}
        statusPending={statusPending}
        onSaved={onSaved}
      />
    )
  }

  return (
    <div className="d-flex flex-column gap-2">
      {rows.map(({ item, index }) => (
        <FlatDestinationItem
          key={`${publisherId}-${item.brand ?? index}`}
          campaignId={campaignId}
          publisherId={publisherId}
          publisherName={publisherName}
          destinationUrls={destinationUrls}
          itemIndex={index}
          getName={getName}
          canEdit={canEdit}
          blockedStatus={blockedStatus}
          statusPending={statusPending}
          rejectionReason={rejectionReason}
          onSaved={onSaved}
        />
      ))}
    </div>
  )
}

export default SubpublisherDestinationSection
